import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import * as IBooks from '../../../../interfaces/merchants/resources/books';
import * as IInputs from '../../../../interfaces/merchants/resources/books/inputs';
import {
  inputBookingTargets,
  RestartReservationError,
} from '../../../../redux/actions/merchants/resources/books/inputs';
import { Dispatch } from '../../../../redux/reducers/merchants/resources/books/inputs';

import scrollToTop from '../../../hoc/scrollToTop';

import * as $ from './helper';

import BooksSubmitButton from '../../../../components/books/common/buttons/BooksSubmitButton';
import BooksLabeledContainer from '../../../../components/books/model1/BooksLabeledContainer';
import BooksSelectedMenu from '../../../../components/books/model1/BooksSelectedMenu';
import BooksSelectedStaff from '../../../../components/books/staffs/BooksSelectedStaff';

import InputBookingTargetCourseSelector from './InputBookingTargetCourseSelector';
import InputBookingTargetOptionSelector from './InputBookingTargetOptionSelector';
import InputBookingTargetStaffSelector from './InputBookingTargetStaffSelector';
import InputBookingTargetSwitch from './InputBookingTargetSwitch';

import css from './InputBookingTargetWithStaff.module.scss';

interface ElementProps {
  modelType: IBooks.ModelType;
  settings: IBooks.SettingBookingTargets;
  postable: boolean;
  onSubmit: () => void;
  onRestart: () => void;
  renew?: boolean;
  withExpressFlow: boolean;
}

interface StateProps {
  inputs: IInputs.State;
}

interface DispatchProps {
  actions: {
    handleSubmit: (
      modelType: IBooks.ModelType,
      merchantId: string,
      resourceId: string,
      course: IBooks.Course,
      options: IBooks.Option[],
      staff: IBooks.Staff,
      selected: IBooks.Switch,
      withExpressFlow: boolean,
      reservationId?: number,
    ) => Promise<void>;
  };
}

interface State {
  selected: IBooks.Switch;
  course?: IBooks.Course;
  options?: IBooks.Option[]; // undefined: 未選択、[]: 0個選択中
  staff?: IBooks.Staff;
  selecting: {
    options?: IBooks.Option[]; // undefined: 未選択、[]: 0個選択中
  };
}

type Props = ElementProps & StateProps & DispatchProps & InjectedIntlProps;

interface StaffProps {
  staff: IBooks.Staff;
  onChangeTarget: (course: boolean, options: boolean, staff?: boolean) => void;
  args: [boolean, boolean, boolean?];
  withBorderBottom?: boolean;
  withBorderTop?: boolean;
  withPaddingLarge?: boolean;
}
const Staff: React.SFC<StaffProps & InjectedIntlProps> = ({
  staff,
  onChangeTarget,
  args,
  withPaddingLarge,
  withBorderBottom,
  intl,
}) => {
  return (
    <BooksSelectedStaff
      name={
        $.isRandomStaff(staff)
          ? intl.formatMessage({ id: 'books.booksMenu.noSelect' })
          : staff.name
      }
      id={staff.id}
      changeStaff={() => onChangeTarget.apply(null, args)}
      withPaddingLarge={withPaddingLarge}
      withBorderBottom={withBorderBottom}
    />
  );
};

interface CurrentCourseOptionsProps {
  course?: IBooks.Course;
  options?: IBooks.Option[];
  onChangeCourse: () => void;
  onChangeOptions: () => void;
}

const CurrentCourseOptions: React.SFC<CurrentCourseOptionsProps &
  InjectedIntlProps> = ({
  course,
  options,
  intl,
  onChangeCourse,
  onChangeOptions,
}) => {
  if (!course && options && options.length === 0) {
    return null;
  }
  return (
    <div>
      {course && (
        <div>
          <BooksLabeledContainer
            label={intl.formatMessage({
              id: 'books.formsLabel.selectedBooks',
            })}
            contentsPadding={true}
          >
            <BooksSelectedMenu
              name={course.name}
              options={options || []}
              price={course.price}
              duration={course.duration}
              changeTarget={onChangeCourse}
              changeOption={onChangeOptions}
            />
          </BooksLabeledContainer>
        </div>
      )}
    </div>
  );
};

interface CurrentStaffCourseProps {
  staff?: IBooks.RealStaff;
  course?: IBooks.Course;
  onChangeStaff: () => void;
  onChangeCourse: () => void;
}

const CurrentStaffCourse: React.SFC<CurrentStaffCourseProps &
  InjectedIntlProps> = ({
  staff,
  course,
  intl,
  onChangeStaff,
  onChangeCourse,
}) => {
  if (!staff && !course) {
    return null;
  }
  return (
    <div className={css.CurrentStaffCourse}>
      {staff && (
        <BooksLabeledContainer
          label={intl.formatMessage({
            id: 'books.formsLabel.selectedBooks',
          })}
          contentsPadding={true}
        >
          <Staff
            staff={staff!}
            onChangeTarget={onChangeStaff}
            args={[false, false, true]}
            withPaddingLarge={true}
            intl={intl}
          />
          {course && (
            <div className={css.CurrentStaffCourse__Menu}>
              <BooksSelectedMenu
                name={course.name}
                options={[]}
                price={course.price}
                duration={course.duration}
                changeTarget={onChangeCourse}
              />
            </div>
          )}
        </BooksLabeledContainer>
      )}
    </div>
  );
};

const showableSwitch = (
  selected: IBooks.Switch,
  course?: IBooks.Course,
  staff?: IBooks.Staff,
): boolean => {
  if (selected === IBooks.Switch.Course && !!course) {
    return false;
  }

  if (selected === IBooks.Switch.Staff && !!staff) {
    return false;
  }
  return true;
};

const selectedBy = (
  settings: IBooks.SettingBookingTargets,
  renew?: boolean,
) => {
  if (renew) {
    return IBooks.Switch.Course;
  }
  if (settings.selected) {
    return settings.selected;
  }
  if (settings.selectedCourse) {
    return IBooks.Switch.Course;
  }
  if (settings.selectedStaff) {
    return IBooks.Switch.Staff;
  }
  return IBooks.Switch.Course;
};

class InputBookingTarget extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const {
      inputs: { course, options, staff },
      settings,
      renew,
    } = props;
    this.state = {
      course: renew ? undefined : course,
      options: renew ? undefined : options,
      selected: selectedBy(settings, renew),
      selecting: { options: renew ? undefined : options },
      staff: renew ? undefined : staff,
    };
    this.handleClickSwitch = this.handleClickSwitch.bind(this);

    this.handleClickCourse = this.handleClickCourse.bind(this);
    this.handleClickOption = this.handleClickOption.bind(this);
    this.handleClickStaff = this.handleClickStaff.bind(this);

    this.handleClearTarget = this.handleClearTarget.bind(this);
    this.handleFixSelection = this.handleFixSelection.bind(this);

    this.handleSubmit = this.handleSubmit.bind(this);
  }

  public componentDidMount() {
    const {
      inputs: { reservationId },
      settings: { selectedCourse, selectedStaff },
    } = this.props;
    // exclude reselect.
    if (!reservationId && selectedCourse) {
      this.handleClickCourse(selectedCourse);
      return;
    }
    // exclude reselect.
    if (!reservationId && selectedStaff) {
      this.handleClickStaff(selectedStaff);
      return;
    }
  }

  private handleClickSwitch(selected: IBooks.Switch) {
    this.setState({ ...this.state, selected });
  }

  private async handleClickCourse(course: IBooks.Course) {
    await this.setState({ ...this.state, course });
  }

  private async handleClickStaff(staff: IBooks.Staff) {
    await this.setState({ ...this.state, staff });
  }

  private handleClickOption(options: IBooks.Option[]) {
    this.setState({ ...this.state, selecting: { options } });
  }

  private handleClearTarget(course: boolean, options: boolean, staff: boolean) {
    this.setState({
      ...this.state,
      course: course ? undefined : this.state.course,
      options: options ? undefined : this.state.options,
      selecting: {},
      staff: staff ? undefined : this.state.staff,
    });
  }

  private async handleFixSelection() {
    await this.setState({
      ...this.state,
      options: this.state.selecting.options || [],
    });
  }

  private async handleSubmit() {
    const {
      modelType,
      onSubmit,
      onRestart,
      inputs: { merchantId, resourceId, reservationId },
      withExpressFlow,
    } = this.props;
    const { selected, course, options, staff } = this.state;
    try {
      await this.props.actions.handleSubmit(
        modelType,
        merchantId!,
        resourceId!,
        course!,
        options || [],
        staff!,
        selected,
        withExpressFlow,
        reservationId,
      );
      onSubmit();
    } catch (e) {
      if (e instanceof RestartReservationError) {
        onRestart();
      }
    }
  }

  public render() {
    const { settings, postable, intl } = this.props;
    const { selected, course, options, staff, selecting } = this.state;

    return (
      <div>
        {showableSwitch(selected, course, staff) && (
          <InputBookingTargetSwitch
            intl={intl}
            selected={selected}
            onClick={this.handleClickSwitch}
          />
        )}
        {selected === IBooks.Switch.Course && (
          <div>
            <CurrentCourseOptions
              course={course}
              options={options}
              onChangeCourse={() => this.handleClearTarget(true, true, true)}
              onChangeOptions={() => this.handleClearTarget(false, true, true)}
              intl={intl}
            />
            {!course ? (
              // [course] -> ...
              <InputBookingTargetCourseSelector
                courses={settings.courses}
                postable={postable}
                onClick={this.handleClickCourse}
              />
            ) : course.options && course.options.length !== 0 && !options ? (
              // course -> [option] -> staff
              <div>
                <BooksLabeledContainer
                  label={intl.formatMessage({
                    id: 'books.formsLabel.addOptions',
                  })}
                  contentsPadding={false}
                  paddingTop={true}
                >
                  <InputBookingTargetOptionSelector
                    options={course.options}
                    checkedOptions={selecting.options || []}
                    onClick={this.handleClickOption}
                  />
                </BooksLabeledContainer>
                <BooksSubmitButton
                  type="button"
                  onClick={this.handleFixSelection}
                  value={intl.formatMessage({
                    id: 'books.submitLabel.selectStaff',
                  })}
                >
                  {intl.formatMessage({ id: 'books.submitLabel.selectStaff' })}
                </BooksSubmitButton>
              </div>
            ) : (
              // course -> [staff]
              // course -> option -> [staff]
              <BooksLabeledContainer
                label={intl.formatMessage({
                  id: 'books.formsLabel.selectStaff',
                })}
                contentsPadding={false}
                paddingTop={true}
                noBorderBottom={true}
              >
                <InputBookingTargetStaffSelector
                  random={course.acceptRandomAssign!}
                  staffs={course.staffIds!.map(id => settings.staffs![id])}
                  postable={postable}
                  onClick={async (_staff: IBooks.Staff) => {
                    await this.handleClickStaff(_staff);
                    await this.handleSubmit();
                  }}
                />
              </BooksLabeledContainer>
            )}
          </div>
        )}
        {selected === IBooks.Switch.Staff && (
          <div>
            <CurrentStaffCourse
              staff={staff as IBooks.RealStaff}
              course={course}
              onChangeStaff={() => this.handleClearTarget(true, true, true)}
              onChangeCourse={() => this.handleClearTarget(true, true, false)}
              intl={intl}
            />
            {!staff ? (
              // [staff] -> ...
              <InputBookingTargetStaffSelector
                random={false}
                staffs={settings.staffList!}
                postable={postable}
                onClick={this.handleClickStaff}
              />
            ) : !course ? (
              // staff -> [course] -> option
              <BooksLabeledContainer
                label={intl.formatMessage({
                  id: 'books.formsLabel.selectMenu',
                })}
                contentsPadding={false}
                paddingTop={true}
                noBorderBottom={true}
              >
                <InputBookingTargetCourseSelector
                  courses={settings.courses!.filter(
                    c =>
                      c.staffIds!.indexOf((staff as IBooks.RealStaff).id) >= 0,
                  )}
                  postable={postable}
                  onClick={async (c: IBooks.Course) => {
                    await this.handleClickCourse(c);
                    if (c.options && c.options.length !== 0) {
                      return;
                    }
                    await this.handleFixSelection();
                    await this.handleSubmit();
                  }}
                />
              </BooksLabeledContainer>
            ) : (
              // staff -> course -> [option]
              <div>
                <BooksLabeledContainer
                  label={intl.formatMessage({
                    id: 'books.formsLabel.addOptions',
                  })}
                  contentsPadding={false}
                  paddingTop={true}
                >
                  <InputBookingTargetOptionSelector
                    options={course.options}
                    checkedOptions={selecting.options || []}
                    onClick={this.handleClickOption}
                  />
                </BooksLabeledContainer>
                <BooksSubmitButton
                  type="button"
                  disabled={!postable}
                  postable={postable}
                  onClick={async () => {
                    await this.handleFixSelection();
                    await this.handleSubmit();
                  }}
                  value={intl.formatMessage({
                    id: 'books.submitLabel.selectDateTime',
                  })}
                >
                  {intl.formatMessage({
                    id: 'books.submitLabel.selectDateTime',
                  })}
                </BooksSubmitButton>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

const mapStateProps = ({
  merchantsResourcesBooksInputs,
}: {
  merchantsResourcesBooksInputs: IInputs.State;
}): StateProps => ({
  inputs: merchantsResourcesBooksInputs,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: {
    handleSubmit: (
      modelType: IBooks.ModelType,
      merchantId: string,
      resourceId: string,
      course: IBooks.Course,
      options: IBooks.Option[],
      staff: IBooks.Staff,
      selected: IBooks.Switch,
      withExpressFlow: boolean,
      reservationId: number,
    ) =>
      inputBookingTargets(
        modelType,
        merchantId,
        resourceId,
        {
          course,
          options,
          staff,
          selected,
        },
        withExpressFlow,
        reservationId,
      )(dispatch),
  },
});

export default connect(
  mapStateProps,
  mapDispatchToProps,
)(injectIntl(scrollToTop(InputBookingTarget)));
